import { useCallback, useEffect, useRef, useState } from 'react';
import { bffQuery, bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';

// ─────────────────────────────────────────────────────────────────────────────
// «صفحهٔ من» — the page builder's one data layer (docs/DOCTOR_PUBLIC_PAGE_DESIGN.md
// §8, BFF typeDefs/doctorPage.js). Both surfaces render from it.
//
// Every write answers with the whole page, which REPLACES the local copy: the
// service decides what was saved (and re-derives the publish blockers and the
// «تغییرات منتشر نشده» flag), so the screen never shows a guess.
//
// Per-card save state: each write names the card that made it (`key`), and
// the hook tracks `saving` / `error` / `savedAt` per key — so the hero card
// can say «ذخیره شد» while the office card is still «در حال ذخیره».
// Writes are never retried automatically (DESIGN_CONCEPT §14.2).
// ─────────────────────────────────────────────────────────────────────────────

const MEDIA = 'id slot fileId url alt sortOrder refId';

export const PAGE_FIELDS = `
  id slug slugLocked templateId theme status
  displayName headline aboutMd
  portrait { ${MEDIA} }
  cover { ${MEDIA} }
  contact
  sections { kind enabled sortOrder titleOverride content supportedByTemplate required }
  articles { id slug slugLocked title bodyMd coverFileId coverUrl status publishedAtMs updatedAtMs hasUnpublishedChanges }
  media { ${MEDIA} }
  publishedAtMs firstPublishedAtMs suspendedReason hasUnpublishedChanges
  publishBlockers { sectionKind field code message }
  councilNumber councilType specialty publicPath publicUrl updatedAtMs
`;

const ARTICLE_FIELDS = 'id slug slugLocked title bodyMd coverFileId coverUrl status publishedAtMs updatedAtMs hasUnpublishedChanges';

export const MY_PAGE = `query MyDoctorPage { myDoctorPage { ${PAGE_FIELDS} } }`;
export const CHECK_SLUG = `query CheckDoctorPageSlug($slug: String!) {
  checkDoctorPageSlug(slug: $slug) { available reason normalized message }
}`;
export const UPDATE_PAGE = `mutation UpdateDoctorPage($input: DoctorPageInput!) {
  updateDoctorPage(input: $input) { ${PAGE_FIELDS} }
}`;
export const UPDATE_SECTION = `mutation UpdateDoctorPageSection($kind: String!, $input: DoctorPageSectionInput!) {
  updateDoctorPageSection(kind: $kind, input: $input) { ${PAGE_FIELDS} }
}`;
export const REORDER = `mutation ReorderDoctorPageSections($kinds: [String!]!) {
  reorderDoctorPageSections(kinds: $kinds) { ${PAGE_FIELDS} }
}`;
export const UPLOAD_IMAGE = `mutation UploadDoctorPageImage($slot: String!, $image: String!, $refId: String, $alt: String) {
  uploadDoctorPageImage(slot: $slot, image: $image, refId: $refId, alt: $alt) { ${PAGE_FIELDS} }
}`;
export const UPDATE_IMAGE = `mutation UpdateDoctorPageImage($mediaId: ID!, $alt: String, $sortOrder: Int) {
  updateDoctorPageImage(mediaId: $mediaId, alt: $alt, sortOrder: $sortOrder) { ${PAGE_FIELDS} }
}`;
export const REMOVE_IMAGE = `mutation RemoveDoctorPageImage($mediaId: ID!) {
  removeDoctorPageImage(mediaId: $mediaId) { ${PAGE_FIELDS} }
}`;
export const UPSERT_ARTICLE = `mutation UpsertDoctorPageArticle($input: DoctorPageArticleInput!) {
  upsertDoctorPageArticle(input: $input) { ${ARTICLE_FIELDS} }
}`;
export const SET_ARTICLE_STATUS = `mutation SetDoctorPageArticleStatus($id: ID!, $status: String!) {
  setDoctorPageArticleStatus(id: $id, status: $status) { ${ARTICLE_FIELDS} }
}`;
export const DELETE_ARTICLE = `mutation DeleteDoctorPageArticle($id: ID!) { deleteDoctorPageArticle(id: $id) }`;
export const PUBLISH = `mutation PublishDoctorPage($complianceAccepted: Boolean!) {
  publishDoctorPage(complianceAccepted: $complianceAccepted) {
    published
    errors { sectionKind field code message }
    page { ${PAGE_FIELDS} }
  }
}`;
export const UNPUBLISH = `mutation UnpublishDoctorPage { unpublishDoctorPage { ${PAGE_FIELDS} } }`;
export const PREVIEW = `mutation CreateDoctorPagePreviewUrl { createDoctorPagePreviewUrl { url expiresAt } }`;
export const LEADS = `query MyDoctorPageLeads($limit: Int, $offset: Int) {
  myDoctorPageLeads(limit: $limit, offset: $offset) {
    total
    items { id name mobile preferredChannel preferredTime message status createdAtMs }
  }
}`;

/** Replace one article inside a page (the article mutations answer with the article only). */
export function withArticle(page, article) {
  if (!page || !article) return page;
  const rest = (page.articles || []).filter((a) => a.id !== article.id);
  return { ...page, articles: [article, ...rest] };
}

export function withoutArticle(page, id) {
  if (!page) return page;
  return { ...page, articles: (page.articles || []).filter((a) => a.id !== id) };
}

/** Pure reducer over the per-card save map — exported for tests. */
export function cardStateReducer(state, action) {
  const prev = state[action.key] || {};
  switch (action.type) {
    case 'start': return { ...state, [action.key]: { ...prev, saving: true, error: null } };
    case 'ok':    return { ...state, [action.key]: { saving: false, error: null, savedAt: action.at } };
    case 'fail':  return { ...state, [action.key]: { ...prev, saving: false, error: action.error } };
    case 'clear': return { ...state, [action.key]: { ...prev, error: null } };
    default: return state;
  }
}

export function useDoctorPage() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cards, setCards] = useState({});
  // The last «انتشار» refusal, shown on the offending cards until the next
  // successful publish or a fresh load.
  const [publishErrors, setPublishErrors] = useState([]);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  // The page as of the last render, so a write can compute — and return — the
  // page it produced (an editor adopts that as its new baseline).
  const pageRef = useRef(null);
  pageRef.current = page;

  const dispatch = useCallback((action) => {
    if (alive.current) setCards((s) => cardStateReducer(s, action));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await bffQuery(MY_PAGE);
      if (!alive.current) return;
      setPage(data?.myDoctorPage || null);
    } catch (err) {
      if (alive.current) setError(err);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /**
   * One write. Resolves `{ ok: true, data, page }` (page = the page it produced)
   * or `{ ok: false, error }` — never throws, because every caller renders the
   * failure on its own card.
   */
  const write = useCallback(async (key, document, variables, apply) => {
    dispatch({ type: 'start', key });
    try {
      const data = await bffMutation(document, variables);
      const next = apply ? (apply(data, pageRef.current) ?? pageRef.current) : pageRef.current;
      if (alive.current && apply) {
        pageRef.current = next;
        setPage(next);
      }
      dispatch({ type: 'ok', key, at: Date.now() });
      return { ok: true, data, page: next };
    } catch (err) {
      dispatch({ type: 'fail', key, error: err });
      return { ok: false, error: err };
    }
  }, [dispatch]);

  const replaceWith = (field) => (data) => data?.[field] || null;

  const actions = {
    updatePage: (key, input) => write(key, UPDATE_PAGE, { input }, replaceWith('updateDoctorPage')),
    updateSection: (key, kind, input) =>
      write(key, UPDATE_SECTION, { kind, input }, replaceWith('updateDoctorPageSection')),
    reorder: (kinds) => write('sections', REORDER, { kinds }, replaceWith('reorderDoctorPageSections')),
    uploadImage: (key, { slot, image, refId, alt }) =>
      write(key, UPLOAD_IMAGE, { slot, image, refId: refId || null, alt: alt || null }, replaceWith('uploadDoctorPageImage')),
    updateImage: (key, mediaId, patch) =>
      write(key, UPDATE_IMAGE, { mediaId, ...patch }, replaceWith('updateDoctorPageImage')),
    removeImage: (key, mediaId) => write(key, REMOVE_IMAGE, { mediaId }, replaceWith('removeDoctorPageImage')),
    upsertArticle: (key, input) =>
      write(key, UPSERT_ARTICLE, { input }, (data, cur) => withArticle(cur, data?.upsertDoctorPageArticle)),
    setArticleStatus: (key, id, status) =>
      write(key, SET_ARTICLE_STATUS, { id, status }, (data, cur) => withArticle(cur, data?.setDoctorPageArticleStatus)),
    deleteArticle: (key, id) =>
      write(key, DELETE_ARTICLE, { id }, (data, cur) => (data?.deleteDoctorPageArticle ? withoutArticle(cur, id) : cur)),
    publish: async (complianceAccepted) => {
      const res = await write('publish', PUBLISH, { complianceAccepted }, (data) => data?.publishDoctorPage?.page || null);
      if (!res.ok) return res;
      const result = res.data?.publishDoctorPage;
      if (alive.current) setPublishErrors(result?.published ? [] : (result?.errors || []));
      return { ok: true, published: !!result?.published, errors: result?.errors || [] };
    },
    unpublish: () => write('publish', UNPUBLISH, {}, replaceWith('unpublishDoctorPage')),
    preview: async () => {
      const res = await write('preview', PREVIEW, {});
      return res.ok ? { ok: true, ...res.data?.createDoctorPagePreviewUrl } : res;
    },
    clearError: (key) => dispatch({ type: 'clear', key }),
  };

  return { page, loading, error, reload: load, cards, publishErrors, actions };
}

/** Availability of a slug the doctor is typing — debounced, latest wins. */
export function useSlugCheck(slug, { enabled, delayMs = 400 } = {}) {
  const [state, setState] = useState({ checking: false, result: null });
  useEffect(() => {
    if (!enabled || !slug) { setState({ checking: false, result: null }); return undefined; }
    let cancelled = false;
    setState((s) => ({ ...s, checking: true }));
    const timer = setTimeout(async () => {
      try {
        const data = await bffQuery(CHECK_SLUG, { slug });
        if (!cancelled) setState({ checking: false, result: data?.checkDoctorPageSlug || null });
      } catch {
        // The service decides on save anyway; a failed probe only loses the hint.
        if (!cancelled) setState({ checking: false, result: null });
      }
    }, delayMs);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [slug, enabled, delayMs]);
  return state;
}

/**
 * «درخواست‌های صفحه». `accumulate` (the phone) appends pages behind
 * «نمایش بیشتر»; otherwise one page at a time for the desktop pager.
 */
export function useDoctorPageLeads({ active, pageSize = 20, accumulate = false } = {}) {
  const [state, setState] = useState({ items: [], total: 0, loading: false, error: null, pageIndex: 0 });

  const fetchPage = useCallback(async (pageIndex, append) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await bffQuery(LEADS, { limit: pageSize, offset: pageIndex * pageSize });
      const list = data?.myDoctorPageLeads || { items: [], total: 0 };
      setState((s) => ({
        items: append ? [...s.items, ...list.items] : list.items,
        total: list.total,
        loading: false,
        error: null,
        pageIndex,
      }));
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: err }));
    }
  }, [pageSize]);

  useEffect(() => { if (active) fetchPage(0, false); }, [active, fetchPage]);

  return {
    ...state,
    loaded: !state.loading && !state.error,
    pageCount: Math.max(1, Math.ceil(state.total / pageSize)),
    hasMore: state.items.length < state.total,
    goTo: (pageIndex) => fetchPage(pageIndex, false),
    loadMore: () => fetchPage(state.pageIndex + 1, accumulate),
    retry: () => fetchPage(state.pageIndex, false),
  };
}
